import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { H2, OL, P, UL } from './ui';

const post: Post = { meta: postMeta('how-to-mix-an-808'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        Most beat battles are won or lost in the low end. If your 808 disappears on a phone or gets
        drowned by the kick, voters move on. Here are the seven mix tricks that consistently keep an
        808 audible across all systems.
      </P>

      <H2 id="trick-1">1. Set the fader at -8 dB</H2>
      <P>
        Drop the 808 channel fader to about -8 dB. That headroom lets you push it later with
        saturation and limiting without clipping the master. Producers who set 808s at 0 dB and try
        to mix around them end up with everything else turned down.
      </P>

      <H2 id="trick-2">2. EQ surgery: 45 boost, 250 cut, 80 notch</H2>
      <UL>
        <li>
          <strong>Boost +3 dB at 45 Hz</strong> for sub weight you feel in the chest.
        </li>
        <li>
          <strong>Cut -4 dB at 250 Hz</strong> to clear out the muddy zone where everything else
          (kick, vocals, snare) lives.
        </li>
        <li>
          <strong>Light notch at 80 Hz</strong> to make room for the kick fundamental.
        </li>
      </UL>

      <H2 id="trick-3">3. Sidechain to the kick</H2>
      <P>
        Compress the 808 with the kick as the sidechain trigger. Settings: ratio 4:1, attack 30ms,
        release 80ms, threshold so you get 4-6 dB of gain reduction on each kick hit. The result is
        the kick punches through cleanly without you having to drop the 808 volume.
      </P>

      <H2 id="trick-4">4. Saturation, lightly</H2>
      <P>
        A touch of saturation (Decapitator, Saturn 2, RC-20, or any free tube emulation) adds upper
        harmonics that make the 808 audible on speakers that cannot reproduce sub bass. Drive
        setting between 2 and 4. Mix knob at 30%. Anything more starts sounding distorted.
      </P>

      <H2 id="trick-5">5. Glide/portamento for movement</H2>
      <P>
        Enable portamento on the 808 synth (or use pitch automation if it is a sample). 50ms glide
        is the sweet spot. The sliding pitch transitions are a defining trap sound and nothing else
        does it.
      </P>

      <H2 id="trick-6">6. Mono below 100 Hz</H2>
      <P>
        Add a stereo imager on the 808 channel and force everything below 100 Hz to mono. Stereo
        bass loses energy on small speakers and cancels on phones held in landscape. Mono bass +
        stereo highs is the most bulletproof mix shape.
      </P>

      <H2 id="trick-7">7. Reference on phone speakers</H2>
      <P>
        Bounce the beat. Send to your phone. Play on phone speakers. If the 808 disappears, your
        saturation is too low or your 250 Hz cut is too aggressive. If the 808 sounds boomy and
        overpowering, your 45 Hz boost is too high or your fader is hot.
      </P>

      <H2 id="recipe">Quick recipe</H2>
      <OL>
        <li>Fader -8 dB</li>
        <li>EQ: +3 at 45 Hz, -4 at 250 Hz, light notch at 80 Hz</li>
        <li>Sidechain to kick: 4:1, 30ms attack, 80ms release</li>
        <li>Saturator: drive 2-4, mix 30%</li>
        <li>Portamento: 50ms</li>
        <li>Stereo imager: mono below 100 Hz</li>
        <li>Reference on phone</li>
      </OL>

      <H2 id="now">Now go submit one</H2>
      <P>
        Apply this 808 chain to your next trap beat and submit it on{' '}
        <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>
        . The fastest test of whether your low end works is a blind audience vote against five other
        producers using the same kit.
      </P>
    </>
  );
}
